import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Clipboard } from '@angular/cdk/clipboard';

import { CreditCommitteeService } from '../../core/credit-committee/credit-committee.service';
import { CcResolution, CcSubjectKind } from '../../core/credit-committee/credit-committee.types';
import { OpsContextService } from '../../core/ops/ops-context.service';
import { IconComponent } from '../icon/icon';
import { StatusChipComponent } from '../status-chip/status-chip';

/**
 * Credit Committee resolution panel (Ops-only surface; N4).
 *
 * Shows the open/passed resolutions for a subject (a KS application or DF
 * facility), the quorum line as the record holds it ("2 of 3 approvals"; met or
 * not is the record's word, never counted here), and — for a logged-in Credit
 * Committee member — Open, Approve and Withdraw. When a resolution passes it
 * exposes the minted `cc_ref`, which a DF reviewer applies to the actual
 * decision (limit review / matter change).
 *
 * FLOW-405 (DOC-82-A §5, UI-DA-82-A-04): opening and withdrawing the record is
 * a member's own act, under the member's name, a withdrawal with its reason —
 * so *Open resolution* and *Withdraw* are offered to a member only (the
 * record's Committee list, not an Ops tier) and everyone else reads the panel.
 * Approve stays visible-but-disabled for non-members (style-guide §4); the
 * server enforces membership on all three.
 */
@Component({
  selector: 'app-cc-resolution-panel',
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    DatePipe,
    IconComponent,
    StatusChipComponent,
  ],
  templateUrl: './cc-resolution-panel.html',
  styleUrl: './cc-resolution-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CcResolutionPanelComponent implements OnInit {
  private readonly ccService = inject(CreditCommitteeService);
  private readonly opsContext = inject(OpsContextService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly clipboard = inject(Clipboard);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  readonly subjectKind = input.required<CcSubjectKind>();
  readonly subjectId = input.required<string>();

  /**
   * Emits the reference of the most recent PASSED resolution (or null) whenever
   * the resolution set changes. The host decision form uses it to pre-fill the
   * cc_ref so the reviewer never has to retype it (P1: cc_ref hardening).
   */
  readonly passedRefChange = output<string | null>();

  protected readonly resolutions = signal<CcResolution[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly approvingId = signal<string | null>(null);
  protected readonly isOpening = signal(false);
  protected readonly showOpenForm = signal(false);
  /** The OPEN resolution a member is withdrawing, and the act in flight. */
  protected readonly withdrawingId = signal<string | null>(null);
  protected readonly isWithdrawing = signal(false);

  protected readonly withdrawForm = this.fb.group({
    reason: ['', [Validators.required, Validators.maxLength(2000)]],
  });

  protected readonly isCcMember = this.opsContext.isCcMember;

  protected readonly openForm = this.fb.group({
    action: ['', [Validators.required, Validators.maxLength(200)]],
    rationale: ['', [Validators.required, Validators.maxLength(2000)]],
  });

  constructor() {
    // Surface the passed reference to the host whenever resolutions change.
    effect(() => {
      this.resolutions();
      this.passedRefChange.emit(this.passedRef());
    });
  }

  /** Whether the quorum is met — the record's line (DOC-82-A §5). */
  protected quorumMet(resolution: CcResolution): boolean {
    return resolution.quorum_met;
  }

  protected toggleWithdraw(resolution: CcResolution): void {
    this.withdrawingId.update((id) => (id === resolution.short_id ? null : resolution.short_id));
    this.withdrawForm.reset();
  }

  /** Withdraw an OPEN resolution — a member's act, recording who, when and why. */
  protected withdraw(resolution: CcResolution): void {
    if (this.withdrawForm.invalid || this.isWithdrawing()) {
      this.withdrawForm.markAllAsTouched();
      return;
    }
    this.isWithdrawing.set(true);
    this.ccService
      .withdraw(resolution.short_id, this.withdrawForm.getRawValue().reason ?? '')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.resolutions.update((rows) =>
            rows.map((r) => (r.short_id === updated.short_id ? updated : r)),
          );
          this.isWithdrawing.set(false);
          this.withdrawingId.set(null);
          this.withdrawForm.reset();
          this.snackBar.open('Resolution withdrawn.', 'Dismiss', { duration: 5000 });
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isWithdrawing.set(false);
          const detail =
            (err as { error?: { detail?: string } })?.error?.detail ??
            'Could not withdraw the resolution.';
          this.snackBar.open(detail, 'Dismiss', { duration: 6000 });
          this.cdr.markForCheck();
        },
      });
  }

  /** Whether an OPEN resolution already exists (only one should be in flight). */
  protected hasOpenResolution(): boolean {
    return this.resolutions().some((r) => r.status === 'OPEN');
  }

  /**
   * Whether the committee has already PASSED a resolution on this matter.
   * A CC resolution is a one-time authority act, not a repeatable advisory
   * step — once one has passed, opening another is a deliberate "supersede"
   * action, never the default. Used to demote the open affordance.
   */
  protected hasPassedResolution(): boolean {
    return this.resolutions().some((r) => r.status === 'PASSED');
  }

  /** The most recent PASSED resolution's reference, for downstream decisions. */
  protected passedRef(): string | null {
    return this.resolutions().find((r) => r.status === 'PASSED')?.cc_ref ?? null;
  }

  protected toggleOpenForm(): void {
    this.showOpenForm.update((v) => !v);
  }

  protected openResolution(): void {
    if (this.openForm.invalid || this.isOpening()) {
      this.openForm.markAllAsTouched();
      return;
    }
    this.isOpening.set(true);
    const v = this.openForm.getRawValue();
    this.ccService
      .open({
        subject_kind: this.subjectKind(),
        subject_id: this.subjectId(),
        action: v.action ?? '',
        rationale: v.rationale ?? '',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (created) => {
          this.resolutions.update((rows) => [created, ...rows]);
          this.isOpening.set(false);
          this.showOpenForm.set(false);
          this.openForm.reset();
          this.snackBar.open('Resolution opened. Members can now record approvals.', 'Dismiss', {
            duration: 5000,
          });
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.isOpening.set(false);
          const detail =
            (err as { error?: { detail?: string } })?.error?.detail ??
            'Could not open the resolution.';
          this.snackBar.open(detail, 'Dismiss', { duration: 6000 });
          this.cdr.markForCheck();
        },
      });
  }

  ngOnInit(): void {
    this.load();
  }

  protected load(): void {
    this.isLoading.set(true);
    this.loadError.set(false);
    this.ccService
      .list({ subject_kind: this.subjectKind(), subject_id: this.subjectId() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.resolutions.set(rows);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  protected approve(resolution: CcResolution): void {
    if (this.approvingId()) return;
    this.approvingId.set(resolution.short_id);
    this.ccService
      .approve(resolution.short_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.resolutions.update((rows) =>
            rows.map((r) => (r.short_id === updated.short_id ? updated : r)),
          );
          this.approvingId.set(null);
          const msg =
            updated.status === 'PASSED'
              ? 'Resolution passed. Reference ready to apply.'
              : 'Approval recorded.';
          this.snackBar.open(msg, 'Dismiss', { duration: 5000 });
          this.cdr.markForCheck();
        },
        error: (err: unknown) => {
          this.approvingId.set(null);
          const detail =
            (err as { error?: { detail?: string } })?.error?.detail ??
            'Could not record your approval.';
          this.snackBar.open(detail, 'Dismiss', { duration: 6000 });
          this.cdr.markForCheck();
        },
      });
  }

  protected copyRef(ref: string): void {
    this.clipboard.copy(ref);
    this.snackBar.open('Reference copied.', 'Dismiss', { duration: 3000 });
  }

  protected progressLabel(resolution: CcResolution): string {
    return `${resolution.approval_count} of ${resolution.quorum_required} approvals`;
  }
}
